import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FlexContent, Numeric, Table, TableCell, TableHeadCell, TableRow } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { LoadDurationClass, ServiceClass } from "../../../../http/screwConnector/types/normativeTables";
import {
  useScrewConnectorKmodValuesApi,
  useScrewConnectorLoadDurationClassesApi,
  useScrewConnectorServiceClassesApi,
} from "../../../../http/screwConnector/useAdminNormativeTables";

// kmod is a small fixed cross table (service class x load duration class, ~3x5 = 15 cells), so it's
// edited as a single matrix grid rather than a list+modal-form CRUD page.
const AdminKmodMatrix: FC = () => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule.normativeTables;
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());

  const serviceClassesApi = useScrewConnectorServiceClassesApi();
  const loadDurationClassesApi = useScrewConnectorLoadDurationClassesApi();
  const kmodApi = useScrewConnectorKmodValuesApi();

  const { data: serviceClassesData } = serviceClassesApi.useServiceClasses({ limit: 100 }, aborter.current.signal);
  const { data: loadDurationClassesData } = loadDurationClassesApi.useLoadDurationClasses(
    { limit: 100, orderBy: "order" },
    aborter.current.signal,
  );
  const { data: kmodData } = kmodApi.useKmodValues({ limit: 400 }, aborter.current.signal);

  const serviceClasses: ServiceClass[] = serviceClassesData?.items ?? [];
  const loadDurationClasses: LoadDurationClass[] = loadDurationClassesData?.items ?? [];

  const [values, setValues] = useState<Record<string, number | null>>({});

  const cellKey = (serviceClassCode: number, loadDurationClassCode: string) => `${serviceClassCode}::${loadDurationClassCode}`;

  useEffect(() => {
    if (!kmodData) return;
    const next: Record<string, number | null> = {};
    for (const row of kmodData.items) {
      next[cellKey(row.serviceClassCode, row.loadDurationClassCode)] = row.kmod;
    }
    setValues(next);
  }, [kmodData]);

  const isReady = useMemo(() => !!serviceClassesData && !!loadDurationClassesData && !!kmodData, [
    serviceClassesData,
    loadDurationClassesData,
    kmodData,
  ]);

  const commitValue = async (serviceClassCode: ServiceClass["code"], loadDurationClassCode: string, kmod: number | null) => {
    if (kmod == null) return;
    try {
      await kmodApi.upsertKmodValue({ serviceClassCode, loadDurationClassCode, kmod });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  if (!isReady) {
    return null;
  }

  return (
    <FlexContent padding direction="column">
      <p>{t.kmod.hint}</p>
      <Table>
        <thead>
          <TableRow>
            <TableHeadCell align="left">{t.serviceClasses.singular}</TableHeadCell>
            {loadDurationClasses.map((loadDurationClass) => (
              <TableHeadCell key={loadDurationClass.code} align="center">
                {loadDurationClass.name}
              </TableHeadCell>
            ))}
          </TableRow>
        </thead>
        <tbody>
          {serviceClasses.map((serviceClass) => (
            <TableRow key={serviceClass.code}>
              <TableCell>{serviceClass.name}</TableCell>
              {loadDurationClasses.map((loadDurationClass) => {
                const key = cellKey(serviceClass.code, loadDurationClass.code);
                return (
                  <TableCell key={loadDurationClass.code} align="center">
                    <Numeric
                      value={values[key] ?? undefined}
                      decimalScale={2}
                      onValueChange={(value) => setValues((prev) => ({ ...prev, [key]: value }))}
                      onBlur={(value) => commitValue(serviceClass.code, loadDurationClass.code, value)}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </tbody>
      </Table>
    </FlexContent>
  );
};

export default AdminKmodMatrix;
